import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaSave, FaTrash, FaEdit, FaTimes } from 'react-icons/fa';

const TicketEventManager = () => {
  const [events, setEvents] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [sponsorMultiplierMap, setSponsorMultiplierMap] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    eventType: 'deposit',
    ticketPrice: '',
    referralTicketsPerFriend: '1',
    rules: '',
    endDate: '',
    isActive: true,
    isVisible: true,
    validSponsorIds: []
  });

  const isReferralEvent = formData.eventType === 'referral';
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!sponsors || sponsors.length === 0) return;
    setSponsorMultiplierMap((prev) => {
      const next = { ...prev };
      sponsors.forEach((s) => {
        const idKey = s.id.toString();
        if (next[idKey] === undefined) {
          next[idKey] = s.ticketMultiplier || s.ticket_multiplier || 1;
        }
      });
      return next;
    });
  }, [sponsors]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const [eventsRes, sponsorsRes] = await Promise.all([
        axios.get('/api/tickets/events/admin', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get('/api/sponsors', {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setEvents(eventsRes.data);
      setSponsors(sponsorsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const resetForm = () => ({
    title: '',
    description: '',
    eventType: 'deposit',
    ticketPrice: '',
    referralTicketsPerFriend: '1',
    rules: '',
    endDate: '',
    isActive: true,
    isVisible: true,
    validSponsorIds: []
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || formData.title.trim() === '') {
      alert('Etkinlik başlığı gereklidir');
      return;
    }

    if (!isReferralEvent) {
      if (
        !formData.ticketPrice ||
        isNaN(parseFloat(formData.ticketPrice)) ||
        parseFloat(formData.ticketPrice) <= 0
      ) {
        alert('Geçerli bir bilet fiyatı giriniz');
        return;
      }
    } else {
      const perFriend = parseInt(formData.referralTicketsPerFriend, 10);
      if (isNaN(perFriend) || perFriend < 1) {
        alert('Her arkadaş için en az 1 bilet giriniz');
        return;
      }
    }

    if (!formData.validSponsorIds || formData.validSponsorIds.length === 0) {
      alert('En az bir geçerli sponsor seçmelisiniz');
      return;
    }

    if (!formData.rules || formData.rules.trim() === '') {
      alert('Kurallar gereklidir');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        alert('Oturum süreniz dolmuş. Lütfen tekrar giriş yapın.');
        setLoading(false);
        return;
      }

      const submitData = new FormData();
      submitData.append('title', formData.title.trim());
      submitData.append('description', formData.description || '');
      submitData.append('eventType', formData.eventType);
      submitData.append(
        'ticketPrice',
        isReferralEvent ? '0' : parseFloat(formData.ticketPrice).toString()
      );
      submitData.append('referralTicketsPerFriend', String(formData.referralTicketsPerFriend || '1'));
      submitData.append('rules', formData.rules || '');
      submitData.append('endDate', formData.endDate || '');
      submitData.append('isActive', formData.isActive ? 'true' : 'false');
      submitData.append('isVisible', formData.isVisible ? 'true' : 'false');
      submitData.append(
        'validSponsorIds',
        JSON.stringify(formData.validSponsorIds.map((id) => parseInt(id, 10)))
      );

      if (imageFile) {
        submitData.append('image', imageFile);
      }

      if (editingEvent) {
        await axios.put(`/api/tickets/events/${editingEvent.id}`, submitData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          timeout: 30000
        });
      } else {
        await axios.post('/api/tickets/events', submitData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          timeout: 30000
        });
      }

      const sponsorUpdates = sponsors
        .filter((s) => formData.validSponsorIds.includes(s.id.toString()))
        .map((s) => {
          const idKey = s.id.toString();
          const desiredMultiplierRaw =
            sponsorMultiplierMap[idKey] ?? (s.ticketMultiplier || s.ticket_multiplier || 1);
          const currentMultiplierRaw = s.ticketMultiplier || s.ticket_multiplier || 1;
          const desiredMultiplier = parseInt(desiredMultiplierRaw, 10) || 1;
          const currentMultiplier = parseInt(currentMultiplierRaw, 10) || 1;
          if (desiredMultiplier === currentMultiplier) return null;
          if (![1, 2, 3, 4].includes(desiredMultiplier)) return null;

          const formDataToSend = new FormData();
          formDataToSend.append('name', s.name || '');
          formDataToSend.append('website_url', s.website_url || '');
          formDataToSend.append('description', s.description || '');
          formDataToSend.append('description_color', s.description_color || '#fbbf24');
          formDataToSend.append('tier', s.tier || '');
          formDataToSend.append('section_id', s.section_id || '');
          formDataToSend.append('order_index', s.order_index || 0);
          formDataToSend.append('size', s.size || 'medium');
          formDataToSend.append('button1_text', s.button1_text || 'Siteye Git');
          formDataToSend.append('button1_link', s.button1_link || '');
          formDataToSend.append('is_active', s.is_active ? 'true' : 'false');
          formDataToSend.append('is_ad_card', s.is_ad_card ? 'true' : 'false');
          formDataToSend.append('logo_url', s.logo_url || '');
          formDataToSend.append('ticket_multiplier', desiredMultiplier.toString());

          return axios.put(`/api/sponsors/${s.id}`, formDataToSend, {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            },
            timeout: 30000
          });
        })
        .filter(Boolean);

      if (sponsorUpdates.length > 0) {
        await Promise.all(sponsorUpdates);
      }

      alert(editingEvent ? 'Etkinlik başarıyla güncellendi!' : 'Etkinlik başarıyla oluşturuldu!');

      setShowForm(false);
      setEditingEvent(null);
      setImageFile(null);
      setImagePreview(null);
      setFormData(resetForm());
      await fetchData();
    } catch (error) {
      console.error('Error saving event:', error);
      let errorMessage = 'Etkinlik kaydedilirken bir hata oluştu';
      if (error.response) {
        errorMessage = error.response.data?.error || error.response.data?.details || errorMessage;
      } else if (error.request) {
        errorMessage = 'Sunucuya bağlanılamadı. Lütfen internet bağlantınızı kontrol edin.';
      } else {
        errorMessage = error.message || errorMessage;
      }
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    const rawValidSponsorIds = event.validSponsorIds ?? event.valid_sponsor_ids ?? '[]';
    let parsedValidSponsorIds = rawValidSponsorIds;
    if (typeof rawValidSponsorIds === 'string') {
      try {
        parsedValidSponsorIds = JSON.parse(rawValidSponsorIds);
      } catch {
        parsedValidSponsorIds = [];
      }
    }
    if (!Array.isArray(parsedValidSponsorIds)) {
      parsedValidSponsorIds = [];
    }
    const normalizedSponsorIds = parsedValidSponsorIds.map((id) => id.toString());
    const evtType = event.eventType || event.event_type || 'deposit';
    setFormData({
      title: event.title,
      description: event.description || '',
      eventType: evtType,
      ticketPrice: event.ticketPrice || event.ticket_price || '',
      referralTicketsPerFriend: String(
        event.referralTicketsPerFriend || event.referral_tickets_per_friend || 1
      ),
      rules: event.rules || '',
      endDate:
        event.endDate || event.end_date
          ? new Date(event.endDate || event.end_date).toISOString().slice(0, 16)
          : '',
      isActive:
        event.isActive !== undefined ? event.isActive : event.is_active === 1 || event.is_active === true,
      isVisible:
        event.isVisible !== undefined ? event.isVisible : event.is_visible === 1 || event.is_visible === true,
      validSponsorIds: normalizedSponsorIds
    });
    const imageUrl = event.imageUrl || event.image_url;
    if (imageUrl) {
      const getImageUrl = (url) => {
        if (!url) return null;
        if (url.startsWith('http://') || url.startsWith('https://')) return url;
        if (url.startsWith('/uploads/')) return url;
        return url.startsWith('/') ? url : `/uploads/${url}`;
      };
      setImagePreview(getImageUrl(imageUrl));
    } else {
      setImagePreview(null);
    }
    setImageFile(null);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu etkinliği silmek istediğinize emin misiniz?')) {
      return;
    }
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`/api/tickets/events/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      console.error('Error deleting event:', error);
      alert('Etkinlik silinirken bir hata oluştu');
    }
  };

  const toggleSponsor = (sponsorId) => {
    const id = sponsorId.toString();
    setFormData((prev) => ({
      ...prev,
      validSponsorIds: prev.validSponsorIds.includes(id)
        ? prev.validSponsorIds.filter((sid) => sid !== id)
        : [...prev.validSponsorIds, id]
    }));
  };

  const setSponsorMultiplierDraft = (sponsorId, multiplier) => {
    const idKey = sponsorId.toString();
    setSponsorMultiplierMap((prev) => ({ ...prev, [idKey]: multiplier }));
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-3xl font-bold text-gold">Bilet Etkinlikleri</h2>
        <button
          type="button"
          onClick={() => {
            setShowForm(true);
            setEditingEvent(null);
            setImageFile(null);
            setImagePreview(null);
            setFormData(resetForm());
          }}
          className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold py-2 px-4 rounded-lg transition-all duration-300"
        >
          Yeni Etkinlik
        </button>
      </div>

      {showForm && (
        <div className="bg-gradient-card rounded-xl p-6 mb-6 border border-gold/30">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gold">
              {editingEvent ? 'Etkinlik Düzenle' : 'Yeni Bilet Etkinliği'}
            </h3>
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                setEditingEvent(null);
              }}
              className="text-white/60 hover:text-white"
            >
              <FaTimes className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-white/80 mb-2">
                Etkinlik Başlığı <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-2 bg-navy-700 border border-gold/30 rounded-lg text-white focus:outline-none focus:border-gold"
                required
              />
            </div>

            <div>
              <label className="block text-white/80 mb-2">Açıklama</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-2 bg-navy-700 border border-gold/30 rounded-lg text-white focus:outline-none focus:border-gold"
                rows={3}
              />
            </div>

            <div>
              <label className="block text-white/80 mb-2">Etkinlik Görseli (1080x1080 önerilir)</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    setImageFile(file);
                    const reader = new FileReader();
                    reader.onloadend = () => setImagePreview(reader.result);
                    reader.readAsDataURL(file);
                  }
                }}
                className="w-full px-4 py-2 bg-navy-700 border border-gold/30 rounded-lg text-white focus:outline-none focus:border-gold"
              />
              {imagePreview && (
                <div className="mt-3">
                  <img
                    src={imagePreview}
                    alt="Önizleme"
                    className="max-w-full h-auto rounded-lg border border-gold/30"
                    style={{ maxHeight: '300px', aspectRatio: '1/1', objectFit: 'contain' }}
                  />
                </div>
              )}
            </div>

            <div>
              <label className="block text-white/80 mb-2">
                Etkinlik Tipi <span className="text-red-400">*</span>
              </label>
              <select
                value={formData.eventType}
                onChange={(e) => setFormData({ ...formData, eventType: e.target.value })}
                className="w-full px-4 py-2 bg-navy-700 border border-gold/30 rounded-lg text-white focus:outline-none focus:border-gold"
              >
                <option value="deposit">Yatırım / Kanıt (klasik)</option>
                <option value="referral">Referans / Arkadaş Daveti</option>
              </select>
            </div>

            {isReferralEvent ? (
              <div>
                <label className="block text-white/80 mb-2">
                  Her onaylanan arkadaş için bilet sayısı <span className="text-red-400">*</span>
                </label>
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={formData.referralTicketsPerFriend}
                  onChange={(e) =>
                    setFormData({ ...formData, referralTicketsPerFriend: e.target.value })
                  }
                  className="w-full px-4 py-2 bg-navy-700 border border-gold/30 rounded-lg text-white focus:outline-none focus:border-gold"
                  required
                />
                <p className="text-white/50 text-sm mt-1">
                  Kullanıcılar sadece site seçer ve kayıt olan arkadaşın kullanıcı adını girer.
                </p>
              </div>
            ) : (
              <div>
                <label className="block text-white/80 mb-2">
                  Bilet Fiyatı (TL) <span className="text-red-400">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.ticketPrice}
                  onChange={(e) => setFormData({ ...formData, ticketPrice: e.target.value })}
                  className="w-full px-4 py-2 bg-navy-700 border border-gold/30 rounded-lg text-white focus:outline-none focus:border-gold"
                  required
                />
              </div>
            )}

            <div>
              <label className="block text-white/80 mb-2">
                Geçerli Sponsorlar <span className="text-red-400">*</span>
              </label>
              <div className="max-h-60 overflow-y-auto bg-navy-800/50 rounded-lg p-4 border border-gold/20">
                {sponsors.map((sponsor) => {
                  const sponsorIdKey = sponsor.id.toString();
                  const isValidSponsor = formData.validSponsorIds.includes(sponsorIdKey);
                  const currentMultiplier = sponsor.ticketMultiplier || sponsor.ticket_multiplier || 1;
                  const draftMultiplier = sponsorMultiplierMap[sponsorIdKey] ?? currentMultiplier;
                  const isMultiplierActive = draftMultiplier > 1;

                  return (
                    <div key={sponsor.id} className="p-2 hover:bg-navy-700/50 rounded">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={isValidSponsor}
                          onChange={() => toggleSponsor(sponsor.id)}
                          className="w-4 h-4 text-gold rounded focus:ring-gold"
                        />
                        <div className="flex-1 text-white font-medium">
                          {sponsor.name}
                          {currentMultiplier > 1 ? ` (${currentMultiplier}x)` : ''}
                        </div>
                      </div>
                      <div className="mt-3 pl-7 pr-2">
                        <div className="flex items-center justify-between gap-3">
                          <div className="text-white/80 text-sm font-semibold">
                            Çarpanlı Bilet Aktif
                          </div>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={isMultiplierActive}
                              disabled={!isValidSponsor}
                              onChange={(e) => {
                                if (!isValidSponsor) return;
                                const nextMultiplier = e.target.checked
                                  ? draftMultiplier > 1
                                    ? draftMultiplier
                                    : 2
                                  : 1;
                                setSponsorMultiplierDraft(sponsor.id, nextMultiplier);
                              }}
                              className="w-5 h-5 text-gold rounded focus:ring-gold disabled:opacity-50"
                            />
                          </label>
                        </div>
                        {isValidSponsor && isMultiplierActive && (
                          <select
                            value={draftMultiplier}
                            onChange={(e) =>
                              setSponsorMultiplierDraft(sponsor.id, parseInt(e.target.value, 10))
                            }
                            className="mt-2 w-full px-3 py-2 bg-navy-700 border border-gold/30 rounded-lg text-white focus:outline-none focus:border-gold"
                          >
                            <option value={2}>2x çarpan</option>
                            <option value={3}>3x çarpan</option>
                            <option value={4}>4x çarpan</option>
                          </select>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-white/80 mb-2">
                Kurallar <span className="text-red-400">*</span>
              </label>
              <textarea
                value={formData.rules}
                onChange={(e) => setFormData({ ...formData, rules: e.target.value })}
                className="w-full px-4 py-2 bg-navy-700 border border-gold/30 rounded-lg text-white focus:outline-none focus:border-gold"
                rows={4}
                required
              />
            </div>

            <div>
              <label className="block text-white/80 mb-2">Bitiş Tarihi (Opsiyonel)</label>
              <input
                type="datetime-local"
                value={formData.endDate}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                className="w-full px-4 py-2 bg-navy-700 border border-gold/30 rounded-lg text-white focus:outline-none focus:border-gold"
              />
            </div>

            <div className="flex gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="w-5 h-5 text-gold rounded focus:ring-gold"
                />
                <span className="text-white">Aktif</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isVisible}
                  onChange={(e) => setFormData({ ...formData, isVisible: e.target.checked })}
                  className="w-5 h-5 text-gold rounded focus:ring-gold"
                />
                <span className="text-white">Görünür</span>
              </label>
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingEvent(null);
                }}
                className="px-6 py-2 bg-navy-700 hover:bg-navy-600 text-white rounded-lg transition-colors"
              >
                İptal
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold rounded-lg transition-all duration-300 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FaSave />
                {loading ? 'Kaydediliyor...' : editingEvent ? 'Güncelle' : 'Kaydet'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="space-y-4">
        {events.map((event) => (
          <div key={event.id} className="bg-gradient-card rounded-xl p-6 border border-gold/30">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2 flex-wrap">
                  <h3 className="text-xl font-bold text-white">{event.title}</h3>
                  {(event.eventType || event.event_type) === 'referral' && (
                    <span className="bg-purple-600 text-white text-xs px-2 py-1 rounded-full">
                      Referans
                    </span>
                  )}
                  {(event.isActive === true || event.is_active === 1) && (
                    <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full">Aktif</span>
                  )}
                  {(event.isVisible === true || event.is_visible === 1) && (
                    <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full">Görünür</span>
                  )}
                </div>
                {event.description && <p className="text-white/70 mb-2">{event.description}</p>}
                <div className="flex gap-4 text-sm text-white/60 flex-wrap">
                  {(event.eventType || event.event_type) === 'referral' ? (
                    <span>
                      Arkadaş başına:{' '}
                      {event.referralTicketsPerFriend || event.referral_tickets_per_friend || 1}{' '}
                      bilet
                    </span>
                  ) : (
                    <span>Bilet Fiyatı: {event.ticketPrice || event.ticket_price}₺</span>
                  )}
                  {event.endDate || event.end_date ? (
                    <span>
                      Bitiş:{' '}
                      {new Date(event.endDate || event.end_date).toLocaleDateString('tr-TR')}
                    </span>
                  ) : (
                    <span>Süresiz</span>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => handleEdit(event)}
                  className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  <FaEdit />
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(event.id)}
                  className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  <FaTrash />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TicketEventManager;